import React from 'react';
import { Button } from '../../components/ui/button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CalendarIcon,
  FileTextIcon,
  ShieldCheckIcon,
  DownloadIcon,
} from 'lucide-react';
import { useTeamRegistrationStore } from '../../store/teamRegistrationStore';
import { useAssociationStore } from '../../store/associationStore';
import { useClubStore } from '../../store/clubStore';

const germanDate = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

const formatGermanDate = (isoDate?: string): string =>
  isoDate ? germanDate.format(new Date(isoDate)) : '';

interface FinalTabProps {
  onNext?: () => void;
  onPrevious?: () => void;
  showNextButton?: boolean;
  showPreviousButton?: boolean;
}

const FinalTab: React.FC<FinalTabProps> = ({
  onPrevious,
  showNextButton = false,
  showPreviousButton = true,
}) => {
  const finalData = useTeamRegistrationStore((state) => state.finalData);
  const association = useAssociationStore((state) => state.association);
  const myClub = useClubStore((state) => state.myClub);

  const handlePreviousStep = () => {
    if (onPrevious) {
      onPrevious();
    }
  };

  return (
    <div className="mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="relative inline-block mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-[#52A445] to-[#52A445]/80 rounded-full flex items-center justify-center shadow-lg">
            <CheckCircleIcon className="w-8 h-8 text-white" />
          </div>
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-white rounded-full flex items-center justify-center shadow-md">
            <div className="w-3 h-3 bg-[#52A445] rounded-full"></div>
          </div>
        </div>

        <h1 className="text-2xl font-bold text-[#52A445] mb-3">
          Registrierung erfolgreich!
        </h1>
        <p className="text-base text-gray-600 max-w-2xl mx-auto">
          Ihre Team-Registrierung wurde erfolgreich abgeschlossen und ist jetzt
          im System gespeichert.
        </p>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-[#52A445]/10 to-[#52A445]/5 px-6 py-4 border-b border-[#52A445]/20">
          <h2 className="text-lg font-semibold text-[#52A445] flex items-center gap-3">
            <ShieldCheckIcon className="w-5 h-5" />
            Bestätigungsdetails
          </h2>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Left Column - Registration Info */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#52A445]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileTextIcon className="w-4 h-4 text-[#52A445]" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm">
                    Melde- und Zahlungsquittung
                  </h3>
                  <p className="text-lg font-mono font-bold text-[#52A445]">
                    {finalData?.transaction_id}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#52A445]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CalendarIcon className="w-4 h-4 text-[#52A445]" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm">
                    Bestätigungsdatum
                  </h3>
                  <p className="text-base font-medium text-gray-600">
                    {formatGermanDate(finalData?.date)}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#52A445]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DownloadIcon className="w-4 h-4 text-[#52A445]" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 mb-1 text-sm">
                    Bestätigung
                  </h3>
                  <p className="text-base text-gray-700">
                    <a
                      href={finalData?.pdf_url}
                      className="flex text-xs text-blue-500 underline items-center gap-2"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Bestätigung herunterladen
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column - Status & Summary */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-[#52A445]/10 to-[#52A445]/5 rounded-lg p-4 border border-[#52A445]/20">
                <h3 className="font-medium text-[#52A445] mb-2 text-sm">
                  Informationen gesendet an:
                </h3>

                <div className="border-t border-[#52A445]/20 pt-3">
                  <div className="space-y-1.5 text-xs text-gray-600">
                    <p>
                      <span className="font-medium text-[#52A445]">
                        {association?.name},{' '}
                      </span>
                      Hat alle Team-Registrierungsdaten erhalten
                    </p>
                    <p>
                      <span className="font-medium text-[#52A445]">
                        {myClub?.sender_name}:{' '}
                      </span>
                      Hat eine Bestätigung mit allen Details erhalten +
                      PDF-Bestätigung per E-Mail
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2 text-sm">
                  Nächste Schritte
                </h3>
                <ul className="text-xs text-gray-600 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#52A445] rounded-full"></div>
                    Bestätigungs-E-Mail erhalten
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#52A445] rounded-full"></div>
                    Turnierplanung wird koordiniert
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-[#52A445] rounded-full"></div>
                    Weitere Informationen folgen
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      {(showNextButton || showPreviousButton) && (
        <div className="flex justify-between items-center">
          {showPreviousButton && (
            <Button
              onClick={handlePreviousStep}
              variant="outline"
              className="px-6 py-2 flex items-center gap-2 border-gray-300 hover:border-[#52A445] hover:bg-[#52A445]/5"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Zurück zur Übersicht
            </Button>
          )}

          <div className="flex-1"></div>

          {showNextButton && (
            <Button
              onClick={() => {}}
              className="px-6 py-2 flex items-center gap-2 bg-[#52A445] hover:bg-[#52A445]/90"
            >
              Weiter
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default FinalTab;
